/* START OF COMPILED CODE */

import BaseContainer from "../../base/BaseContainer";
import Interactive from "../../components/Interactive";
import NineSlice from "../../components/NineSlice";
import SingleButton from "./buttons/SingleButton";
import { Button } from "@components/components";
/* START-USER-IMPORTS */
/* END-USER-IMPORTS */

/**
 * RewardPackPrompt - CPI-style reward pack claiming prompt
 * Displays available reward packs and allows players to claim them after login
 */
export default class RewardPackPrompt extends BaseContainer {

    constructor(scene, x, y) {
        super(scene, x ?? 760, y ?? 480);

        /** @type {Phaser.GameObjects.NineSlice} */
        this.bg;
        /** @type {Phaser.GameObjects.Text} */
        this.titleText;
        /** @type {Phaser.GameObjects.Text} */
        this.descriptionText;
        /** @type {Phaser.GameObjects.Text} */
        this.bannerText;
        /** @type {Phaser.GameObjects.Container} */
        this.rewardsContainer;
        /** @type {Phaser.GameObjects.Image} */
        this.claimButton;
        /** @type {Phaser.GameObjects.Container} */
        this.successContainer;

        this.visible = false;
        this.pendingPacks = [];
        this.currentPack = null;
        this.claimCallback = null;

        // Block input to background
        const block = scene.add.rectangle(0, 0, 1520, 960);
        block.isFilled = true;
        block.fillColor = 0;
        block.fillAlpha = 0.4;
        this.add(block);

        // Main background panel using nine-slice
        const bg = scene.add.nineslice(0, 0, "prompt", "window", 700, 500, 44, 39, 40, 41);
        this.add(bg);

        // Banner background - use a graphics-drawn rounded rectangle
        const bannerGraphics = scene.add.graphics();
        bannerGraphics.fillStyle(0x1a5b99, 1);
        bannerGraphics.fillRoundedRect(-200, -220, 400, 55, 10);
        bannerGraphics.lineStyle(3, 0x0d3d6b, 1);
        bannerGraphics.strokeRoundedRect(-200, -220, 400, 55, 10);
        this.add(bannerGraphics);

        // Banner text (e.g., "WELCOME GIFT!")
        const bannerText = scene.add.text(0, -193, "REWARD PACK!", {
            fontFamily: "CCFaceFront",
            fontSize: "32px",
            fontStyle: "bold italic",
            color: "#ffffff",
            align: "center",
            stroke: "#003366",
            strokeThickness: 6
        });
        bannerText.setOrigin(0.5, 0.5);
        this.add(bannerText);

        // Title text
        const titleText = scene.add.text(0, -130, "You've received a gift!", {
            fontFamily: "Burbank Small",
            fontSize: "32px",
            color: "#ffffff",
            align: "center",
            stroke: "#003366",
            strokeThickness: 5
        });
        titleText.setOrigin(0.5, 0.5);
        this.add(titleText);

        // Description text
        const descriptionText = scene.add.text(0, -90, "", {
            fontFamily: "Arial",
            fontSize: "20px",
            color: "#ffffff",
            align: "center",
            stroke: "#003366",
            strokeThickness: 3,
            wordWrap: { width: 600 }
        });
        descriptionText.setOrigin(0.5, 0.5);
        this.add(descriptionText);

        // Rewards container (for displaying items/coins)
        const rewardsContainer = scene.add.container(0, 30);
        this.add(rewardsContainer);

        // Claim button using proper game button texture
        const claimButton = scene.add.image(0, 180, "prompt", "window-button");
        claimButton.setScale(0.85);
        this.add(claimButton);

        // Claim button text
        const claimBtnText = scene.add.text(0, 180, "Claim", {
            fontFamily: "CCFaceFront",
            fontSize: "32px",
            fontStyle: "bold italic",
            color: "#ffffff",
            align: "center",
            stroke: "#003366",
            strokeThickness: 5
        });
        claimBtnText.setOrigin(0.5, 0.5);
        this.add(claimBtnText);

        // Button component
        const claimButtonComponent = new Button(claimButton);
        claimButtonComponent.spriteName = "window-button";
        claimButtonComponent.callback = () => this.claimCurrentPack();

        // Success overlay (hidden by default)
        const successContainer = scene.add.container(0, 0);
        successContainer.visible = false;
        this.add(successContainer);

        const successBg = scene.add.nineslice(0, 0, "prompt", "window", 700, 500, 44, 39, 40, 41);
        successContainer.add(successBg);

        // Success checkmark using game graphics
        const checkCircle = scene.add.circle(0, -80, 50, 0x4CAF50);
        successContainer.add(checkCircle);
        
        const checkmark = scene.add.text(0, -80, "✓", {
            fontFamily: "Arial",
            fontSize: "60px",
            color: "#ffffff",
            fontStyle: "bold"
        });
        checkmark.setOrigin(0.5, 0.5);
        successContainer.add(checkmark);

        const successText = scene.add.text(0, 10, "Rewards Claimed!", {
            fontFamily: "CCFaceFront",
            fontSize: "36px",
            fontStyle: "bold italic",
            color: "#ffffff",
            stroke: "#003366",
            strokeThickness: 6,
            padding: { left: 10, right: 10 }
        });
        successText.setOrigin(0.5, 0.5);
        successContainer.add(successText);

        // Continue button
        const continueBtn = scene.add.image(0, 130, "prompt", "window-button");
        continueBtn.setScale(0.85);
        successContainer.add(continueBtn);

        const continueText = scene.add.text(0, 130, "Continue", {
            fontFamily: "CCFaceFront",
            fontSize: "32px",
            fontStyle: "bold italic",
            color: "#ffffff",
            stroke: "#003366",
            strokeThickness: 5
        });
        continueText.setOrigin(0.5, 0.5);
        successContainer.add(continueText);

        const continueBtnComponent = new Button(continueBtn);
        continueBtnComponent.spriteName = "window-button";
        continueBtnComponent.callback = () => this.onSuccessContinue();

        // Block input
        new Interactive(block);

        this.bg = bg;
        this.titleText = titleText;
        this.descriptionText = descriptionText;
        this.bannerText = bannerText;
        this.rewardsContainer = rewardsContainer;
        this.claimButton = claimButton;
        this.claimBtnText = claimBtnText;
        this.successContainer = successContainer;

        /* START-USER-CTR-CODE */
        /* END-USER-CTR-CODE */
    }

    /* START-USER-CODE */

    /**
     * Show the reward pack prompt with the given packs
     * @param {Array} packs - Array of eligible reward packs from WebAPI
     * @param {Function} onComplete - Callback when all packs have been processed
     */
    show(packs, onComplete = null) {
        if (!packs || packs.length === 0) {
            console.log('[RewardPackPrompt] No packs to show');
            if (onComplete) onComplete();
            return;
        }

        this.pendingPacks = [...packs];
        this.claimCallback = onComplete;

        // Hide HTML overlay z-index so this prompt appears on top
        this.interface?.prompt?.hideOverlayZIndex();

        this.showNextPack();
        super.show();
    }

    /**
     * Show the next pack in the queue
     */
    showNextPack() {
        if (this.pendingPacks.length === 0) {
            this.onAllPacksProcessed();
            return;
        }

        this.currentPack = this.pendingPacks.shift();
        this.successContainer.visible = false;
        this.displayPack(this.currentPack);
    }

    /**
     * Display a single reward pack
     * @param {Object} pack - The reward pack to display
     */
    displayPack(pack) {
        console.log('[RewardPackPrompt] Displaying pack:', pack);
        
        // Update banner text (server sends both banner_text and bannerText)
        this.bannerText.text = pack.bannerText || pack.banner_text || "REWARD PACK!";

        // Update title
        this.titleText.text = pack.name || "You've received a gift!";

        // Update description
        this.descriptionText.text = pack.description || "";

        // Clear previous rewards display
        this.rewardsContainer.removeAll(true);

        // Display rewards
        const contents = pack.contents || {};
        console.log('[RewardPackPrompt] Pack contents:', contents);
        
        let xOffset = 0;
        const spacing = 140;
        const rewards = [];

        // Calculate total rewards to center them
        if (contents.coins && contents.coins > 0) rewards.push({ type: 'coins', value: contents.coins });
        if (contents.items?.length > 0) contents.items.forEach(id => rewards.push({ type: 'item', value: id }));
        if (contents.furniture?.length > 0) contents.furniture.forEach(id => rewards.push({ type: 'furniture', value: id }));
        if (contents.emojis?.length > 0) contents.emojis.forEach(id => rewards.push({ type: 'emoji', value: id }));

        console.log('[RewardPackPrompt] Rewards to display:', rewards);

        const totalWidth = (rewards.length - 1) * spacing;
        xOffset = -totalWidth / 2;

        rewards.forEach((reward, index) => {
            const rewardX = xOffset + (index * spacing);
            this.createRewardDisplay(reward.type, reward.value, rewardX, 0);
        });
    }

    /**
     * Create a visual display for a reward
     * @param {string} type - Type of reward (coins, item, furniture, emoji)
     * @param {number} value - ID or amount
     * @param {number} x - X position
     * @param {number} y - Y position
     */
    createRewardDisplay(type, value, x, y) {
        const container = this.scene.add.container(x, y);
        
        // Background circle - larger
        const circle = this.scene.add.circle(0, 0, 55, 0xffffff, 0.3);
        container.add(circle);

        if (type === 'coins') {
            // Use the coin sprite from the game assets
            try {
                const coinIcon = this.scene.add.image(0, 0, "main-stampcoins", "coins");
                coinIcon.setScale(0.6);
                container.add(coinIcon);
            } catch (e) {
                // Fallback to a styled coin representation
                const coinCircle = this.scene.add.circle(0, 0, 30, 0xFFD700);
                container.add(coinCircle);
                const coinInner = this.scene.add.circle(0, 0, 22, 0xFFB300);
                container.add(coinInner);
                const coinSymbol = this.scene.add.text(0, 0, "$", {
                    fontFamily: "Arial",
                    fontSize: "28px",
                    fontStyle: "bold",
                    color: "#996600"
                });
                coinSymbol.setOrigin(0.5, 0.5);
                container.add(coinSymbol);
            }

            // Coin amount label
            const coinAmount = this.scene.add.text(0, 55, `${value.toLocaleString()}`, {
                fontFamily: "Burbank Small",
                fontSize: "22px",
                color: "#ffffff",
                fontStyle: "bold",
                stroke: "#003366",
                strokeThickness: 4
            });
            coinAmount.setOrigin(0.5, 0.5);
            container.add(coinAmount);
        } else if (type === 'item') {
            // Try to load item icon
            const itemData = this.crumbs?.items?.[value];
            const itemName = itemData?.name || `Item #${value}`;
            
            // Load the item icon if available
            const iconKey = `clothing/icon/240/${value}`;
            const iconUrl = `assets/media/clothing/icon/240/${value}.png`;
            
            if (this.scene.textures.exists(iconKey)) {
                const icon = this.scene.add.image(0, 0, iconKey);
                icon.setScale(0.45); // Larger scale
                container.add(icon);
            } else {
                // Placeholder while loading
                const placeholderBg = this.scene.add.circle(0, 0, 35, 0x999999, 0.5);
                container.add(placeholderBg);
                
                const placeholderText = this.scene.add.text(0, 0, "?", {
                    fontFamily: "Arial",
                    fontSize: "32px",
                    fontStyle: "bold",
                    color: "#ffffff"
                });
                placeholderText.setOrigin(0.5, 0.5);
                container.add(placeholderText);

                // Try to load the icon
                this.scene.load.image(iconKey, iconUrl);
                this.scene.load.once('complete', () => {
                    if (this.scene.textures.exists(iconKey)) {
                        placeholderBg.destroy();
                        placeholderText.destroy();
                        const icon = this.scene.add.image(0, 0, iconKey);
                        icon.setScale(0.45);
                        container.add(icon);
                    }
                });
                this.scene.load.start();
            }

            // Item name label
            const label = this.scene.add.text(0, 60, itemName.length > 14 ? itemName.substring(0, 12) + "..." : itemName, {
                fontFamily: "Arial",
                fontSize: "16px",
                color: "#ffffff",
                fontStyle: "bold",
                stroke: "#003366",
                strokeThickness: 3
            });
            label.setOrigin(0.5, 0.5);
            container.add(label);
        } else if (type === 'furniture') {
            const furnitureData = this.crumbs?.furniture?.[value];
            const furnitureName = furnitureData?.name || `Furniture`;

            // Try to load furniture icon
            const iconKey = `furniture/icon/@5x/${value}`;
            const iconUrl = `assets/media/furniture/icon/@5x/${value}.png`;

            if (this.scene.textures.exists(iconKey)) {
                const icon = this.scene.add.image(0, 0, iconKey);
                icon.setScale(0.8);
                container.add(icon);
            } else {
                // Furniture placeholder
                const placeholderBg = this.scene.add.circle(0, 0, 35, 0x8B4513, 0.6);
                container.add(placeholderBg);
                
                const placeholderText = this.scene.add.text(0, 0, "F", {
                    fontFamily: "Arial",
                    fontSize: "28px",
                    fontStyle: "bold",
                    color: "#ffffff"
                });
                placeholderText.setOrigin(0.5, 0.5);
                container.add(placeholderText);

                // Try to load
                this.scene.load.image(iconKey, iconUrl);
                this.scene.load.once('complete', () => {
                    if (this.scene.textures.exists(iconKey)) {
                        placeholderBg.destroy();
                        placeholderText.destroy();
                        const icon = this.scene.add.image(0, 0, iconKey);
                        icon.setScale(0.8);
                        container.add(icon);
                    }
                });
                this.scene.load.start();
            }

            const label = this.scene.add.text(0, 60, furnitureName.length > 14 ? furnitureName.substring(0, 12) + "..." : furnitureName, {
                fontFamily: "Arial",
                fontSize: "16px",
                color: "#ffffff",
                fontStyle: "bold",
                stroke: "#003366",
                strokeThickness: 3
            });
            label.setOrigin(0.5, 0.5);
            container.add(label);
        } else if (type === 'emoji') {
            // Emoji placeholder
            const emojiBg = this.scene.add.circle(0, 0, 35, 0xFFEB3B, 0.8);
            container.add(emojiBg);
            
            const emojiText = this.scene.add.text(0, 0, "😊", {
                fontSize: "40px"
            });
            emojiText.setOrigin(0.5, 0.5);
            container.add(emojiText);

            const label = this.scene.add.text(0, 60, "Emoji", {
                fontFamily: "Arial",
                fontSize: "16px",
                color: "#ffffff",
                fontStyle: "bold",
                stroke: "#003366",
                strokeThickness: 3
            });
            label.setOrigin(0.5, 0.5);
            container.add(label);
        }

        this.rewardsContainer.add(container);
    }

    /**
     * Claim the current pack via WebAPI
     */
    async claimCurrentPack() {
        if (!this.currentPack) {
            console.error('[RewardPackPrompt] No current pack to claim');
            return;
        }

        try {
            // Disable claim button during request
            this.claimButton.visible = false;
            this.claimBtnText.visible = false;

            const webapi = this.network?.webapi;
            if (!webapi?.isAuthenticated()) {
                console.error('[RewardPackPrompt] WebAPI not authenticated');
                this.interface.prompt.showError('Unable to claim rewards. Please try again.');
                this.claimButton.visible = true;
                this.claimBtnText.visible = true;
                return;
            }

            const result = await webapi.claimRewardPack(this.currentPack.id);

            if (result.success !== false && !result.error) {
                // Update local inventory with claimed items
                const claimed = result.claimedItems || this.currentPack.contents || {};
                
                // Add items to inventory
                if (claimed.items?.length > 0) {
                    claimed.items.forEach(itemId => {
                        const item = parseInt(itemId);
                        const data = this.crumbs?.items?.[item];
                        const slot = data?.slot || 'body';
                        if (!this.world.client.inventory[slot].includes(item)) {
                            this.world.client.inventory[slot].push(item);
                            this.world.client.inventory[slot].sort((a, b) => a - b);
                        }
                    });
                }

                // Add furniture to inventory
                if (claimed.furniture?.length > 0) {
                    claimed.furniture.forEach(furnitureId => {
                        const id = parseInt(furnitureId);
                        const existing = this.world.client.furniture.find(f => f.furnitureId === id);
                        if (existing) {
                            existing.quantity = (existing.quantity || 1) + 1;
                        } else {
                            this.world.client.furniture.push({ furnitureId: id, quantity: 1 });
                        }
                    });
                }

                // Update coins
                if (claimed.coins > 0 || result.coins !== undefined) {
                    this.world.client.coins = result.coins ?? (this.world.client.coins + (claimed.coins || 0));
                }

                // Sync the claimed items with the game server's in-memory inventory
                // This ensures the server knows about the items without requiring relog
                this.network.send('sync_reward_pack', {
                    items: claimed.items || [],
                    furniture: claimed.furniture || [],
                    coins: this.world.client.coins
                });

                // Show success
                this.showSuccess();
            } else {
                console.error('[RewardPackPrompt] Claim failed:', result.error);
                this.interface.prompt.showError(result.error || 'Failed to claim rewards.');
                this.claimButton.visible = true;
                this.claimBtnText.visible = true;
            }
        } catch (error) {
            console.error('[RewardPackPrompt] Claim error:', error);
            this.interface.prompt.showError('Failed to claim rewards. Please try again.');
            this.claimButton.visible = true;
            this.claimBtnText.visible = true;
        }
    }

    /**
     * Show success state after claiming
     */
    showSuccess() {
        this.successContainer.visible = true;
    }

    /**
     * Handle continue button after success
     */
    onSuccessContinue() {
        this.successContainer.visible = false;
        this.claimButton.visible = true;
        this.claimBtnText.visible = true;
        
        // Check for more packs
        if (this.pendingPacks.length > 0) {
            this.showNextPack();
        } else {
            this.onAllPacksProcessed();
        }
    }

    /**
     * Called when all packs have been processed
     */
    onAllPacksProcessed() {
        this.currentPack = null;
        
        // Restore HTML overlay z-index when closing
        this.interface?.prompt?.showOverlayZIndex();
        
        // Refresh player card to show new items
        if (this.interface?.refreshPlayerCard) {
            this.interface.refreshPlayerCard();
        }

        super.close();

        if (this.claimCallback) {
            this.claimCallback();
            this.claimCallback = null;
        }
    }

    close() {
        // Don't allow manual close if there are pending packs
        if (this.pendingPacks.length > 0 || this.currentPack) {
            return;
        }

        // Restore HTML overlay z-index when closing
        this.interface?.prompt?.showOverlayZIndex();

        super.close();
    }

    /* END-USER-CODE */
}

/* END OF COMPILED CODE */
